import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Select, Switch } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { relativeTime } from '@/lib/format';
import { getTeam, inviteMember, removeMember, revokeInvite, setMemberRole } from '@/data/client';
import type { Role } from '@/data/types';
import { useMe } from '@/auth/useMe';
import { FormError, Modal } from '@/components/crm/Modal';

export const ROLE_LABEL: Record<Role, string> = {
  owner: 'Owner', admin: 'Admin', sales_manager: 'Sales manager', sales_rep: 'Sales rep', project_manager: 'Project manager', staff: 'Staff',
};
const ROLE_OPTIONS = (Object.keys(ROLE_LABEL) as Role[]).map((value) => ({ value, label: ROLE_LABEL[value] }));

export function TeamSettings() {
  const qc = useQueryClient();
  const { me } = useMe();
  const { data } = useQuery({ queryKey: ['team'], queryFn: getTeam });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('sales_rep');
  const [createLogin, setCreateLogin] = useState(false);
  const [removing, setRemoving] = useState<{ userId: string; label: string } | null>(null);
  const refresh = () => { for (const k of ['team', 'members', 'audit']) void qc.invalidateQueries({ queryKey: [k] }); };

  const invite = useMutation({
    mutationFn: () => inviteMember({ email, role, createLogin }),
    onSuccess: () => { setEmail(''); setCreateLogin(false); refresh(); },
  });
  const changeRole = useMutation({ mutationFn: (v: { userId: string; role: Role }) => setMemberRole(v.userId, v.role), onSuccess: refresh });
  const revoke = useMutation({ mutationFn: revokeInvite, onSuccess: refresh });
  const remove = useMutation({ mutationFn: removeMember, onSuccess: () => { setRemoving(null); refresh(); } });
  const ownerOnly = (r: Role) => r === 'owner' && me?.role !== 'owner';

  return (
    <div className="crm-list">
      <Card padding={false} className="crm-section">
        <div className="crm-section__head"><h3>Invite someone</h3></div>
        <form className="crm-invite" onSubmit={(e) => { e.preventDefault(); invite.mutate(); }}>
          <Input label="Email" type="email" placeholder="name@tritoncommerce.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Select label="Role" options={ROLE_OPTIONS.filter((o) => !ownerOnly(o.value))} value={role} onChange={(e) => setRole(e.target.value as Role)} />
          <Button type="submit" disabled={!email.trim() || invite.isPending}>{invite.isPending ? 'Inviting…' : 'Invite'}</Button>
        </form>
        <Switch label="Also send a Triton login invitation (for people who don't have a tritonIQ / clientIQ login yet)" checked={createLogin} onChange={(e) => setCreateLogin(e.target.checked)} />
        <FormError error={invite.error} />
        {invite.isSuccess && <InviteResult login={invite.data.login} />}
        <p className="crm-cell-sub" style={{ marginTop: 10 }}>They join the first time they sign in at {window.location.origin} with that email. Nothing else is needed for staff who already use tritonIQ.</p>
      </Card>

      <Card padding={false}>
        <table className="dash-table">
          <thead><tr><th>Member</th><th>Role</th><th>Google</th><th>Joined</th><th /></tr></thead>
          <tbody>
            {data?.members.map((m) => {
              const self = m.userId === me?.userId;
              const locked = self || ownerOnly(m.role);
              return (
                <tr key={m.userId}>
                  <td><strong>{m.name ?? m.email}</strong>{m.name && <span className="crm-cell-sub">{m.email}</span>}</td>
                  <td>
                    <select className="crm-inline-select" aria-label={`Role for ${m.email}`} value={m.role} disabled={locked || changeRole.isPending}
                      title={self ? "You can't change your own role" : undefined}
                      onChange={(e) => changeRole.mutate({ userId: m.userId, role: e.target.value as Role })}>
                      {ROLE_OPTIONS.filter((o) => o.value === m.role || !ownerOnly(o.value)).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                  <td className="crm-cell-muted">{m.googleConnected ? 'Connected' : '—'}</td>
                  <td className="crm-cell-muted">{relativeTime(m.joinedAt)}</td>
                  <td className="crm-num">
                    {!locked && (
                      <button type="button" className="lead-modal__x" aria-label={`Remove ${m.email}`} onClick={() => setRemoving({ userId: m.userId, label: m.name ?? m.email })}>
                        <Icon name="trash" size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {data?.invites.map((i) => (
              <tr key={i.id}>
                <td><strong>{i.email}</strong><span className="crm-cell-sub">Invited {relativeTime(i.createdAt)} — not signed in yet</span></td>
                <td className="crm-cell-muted">{ROLE_LABEL[i.role]}</td>
                <td /><td />
                <td className="crm-num">
                  <Button variant="ghost" size="sm" onClick={() => revoke.mutate(i.id)} disabled={revoke.isPending}>Revoke</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <FormError error={changeRole.error ?? revoke.error} />

      {removing && (
        <Modal title={`Remove ${removing.label}?`} onClose={() => setRemoving(null)} footer={<>
          <Button variant="ghost" onClick={() => setRemoving(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => remove.mutate(removing.userId)} disabled={remove.isPending}>Remove</Button>
        </>}>
          <p>They lose access to clientIQ immediately and their Google connection is deleted. Companies, contacts and deals they own stay and show as unassigned. You can invite them again later.</p>
          <FormError error={remove.error} />
        </Modal>
      )}
    </div>
  );
}

function InviteResult({ login }: { login: { result: string; message?: string } | null }) {
  if (!login) return <p className="crm-cell-sub">Invite saved.</p>;
  if (login.result === 'invited') return <p className="crm-cell-sub">Invite saved, and Clerk emailed them a link to create their login.</p>;
  if (login.result === 'existing') return <p className="crm-cell-sub">Invite saved. They already have a Triton login, so no email was needed.</p>;
  return <p className="crm-error">Invite saved, but the login invitation failed: {login.message}</p>;
}
